import { Form, Input, Button, Select, DatePicker, Row, Col, Modal, message } from 'antd';
import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import type { RangePickerProps } from 'antd/es/date-picker';
import styles from '../index.module.scss';
import ExportListModalContent from './exportListModal';
import { exportFleetReportV2Request, getFleetReportV2Request, getInsurerOptionsRequest } from '@/service/fleetRisk';
import type { InsuranceOption } from '@/service/fleetRisk';
import { useFleetAndCarSelection } from '../hooks/useFleetAndCarSelection.hook';
import { on } from 'events';
const { RangePicker } = DatePicker;


interface iFleetReportV2Params {
  onSearch?: (params: any) => void;
  onExport?: (params: any) => void;
  isSearching?: boolean;
  isExporting?: boolean;
}

// 车辆报表筛选查询表单
export default function FilterForm(props: iFleetReportV2Params) {
  const { onSearch, onExport, isSearching, isExporting } = props;
  const [form] = Form.useForm();
  // const [isModalVisible, setIsModalVisible] = useState(false);

  // 使用自定义hook管理车队和车辆选择
  const { allCompanyList, carOptions, loading, fetchCarList } = useFleetAndCarSelection();

  // 服务商（保司）选项
  const [insurerOptions, setInsurerOptions] = useState<{ label: string; value: string }[]>([]);
  // 服务商完整列表，用于根据选中名称查找 id（供车辆列表 insuranceId 查询使用）
  const [insurerList, setInsurerList] = useState<InsuranceOption[]>([]);
  useEffect(() => {
    getInsurerOptionsRequest()
      .then(res => {
        const list: InsuranceOption[] = res?.data?.data || [];
        setInsurerList(list);
        setInsurerOptions(list.map(item => ({ label: item.name, value: item.name })));
      })
      .catch(err => {
        console.error('获取服务商选项失败:', err);
      });
  }, []);

  // 根据服务商名称查找对应的 id
  const getInsuranceIdByName = (name?: string): number | undefined => {
    if (!name) return undefined;
    return insurerList.find(item => item.name === name)?.id;
  };

  // 车队变化：刷新车辆列表并清空已选车辆
  const handleFleetChange = async (selectedFleets: string[]) => {
    const insuranceName = form.getFieldValue('insurance_company');
    const insuranceId = getInsuranceIdByName(insuranceName);
    await fetchCarList(selectedFleets, insuranceId);
    // 清空已选车辆
    form.setFieldsValue({ plate_numbers: [] });
    // 重新验证报告显示名称字段，因为验证规则依赖于车队数量
    form.validateFields(['report_name']).catch(() => {
      // 忽略验证错误，因为可能是清空导致的
    });
  };

  // 服务商变化：刷新车辆列表并清空已选车辆
  const handleInsurerChange = async (insuranceName?: string) => {
    const fleetNames: string[] = form.getFieldValue('fleet_names') || [];
    const insuranceId = getInsuranceIdByName(insuranceName);
    await fetchCarList(fleetNames, insuranceId);
    // 清空已选车辆
    form.setFieldsValue({ plate_numbers: [] });
  };

  // 时间范围变化时的验证
  const handleDateRangeChange = (dates: any) => {
    if (dates && dates.length === 2) {
      const [startDate, endDate] = dates;
      const daysDiff = endDate.diff(startDate, 'days');
      if (daysDiff >= 7) {
        message.warning('时间范围不能超过7天');
        form.setFieldsValue({ reportPeriod: null });
        return;
      }
    }
  };

  // 报告显示名称的动态验证规则
  const getReportNameRules = () => {
    return [
      {
        validator: (_: any, value: string) => {
          const selectedFleets = form.getFieldValue('fleet_names') || [];
          // 如果选中车队数量大于1，则报告显示名称必填
          if (selectedFleets.length > 1) {
            if (!value || value.trim() === '') {
              return Promise.reject(new Error('选择多个车队时，报告显示名称为必填项'));
            }
          }
          return Promise.resolve();
        }
      }
    ];
  };


  const _handleFormValue = (values: any) => {
    const { fleet_names, plate_numbers, reportPeriod, report_name, insurance_company } = values;
    // 验证时间范围是否超过7天
    if (reportPeriod && reportPeriod.length === 2) {
      const [startDate, endDate] = reportPeriod;
      const daysDiff = endDate.diff(startDate, 'days');
      if (daysDiff >= 7) {
        message.error('时间范围不能超过7天');
        return;
      }
    }
    let start_date = '';
    let end_date = '';
    if (reportPeriod && reportPeriod.length === 2) {
      start_date = reportPeriod[0].format('YYYY-MM-DD');
      end_date = reportPeriod[1].format('YYYY-MM-DD');
    }

    // 如果report_name为空，则使用fleet_names转换为字符串
    let finalReportName = report_name;
    if (!report_name || report_name.trim() === '') {
      finalReportName = fleet_names && fleet_names.length > 0 ? fleet_names.join(', ') : '';
    }

    return {
      fleet_names,
      plate_numbers,
      start_date,
      end_date,
      report_name: finalReportName,
      // 服务商名称，非必填，未选时不传
      insurance_company: insurance_company || undefined,
    };
  };

  const handleSearch = () => {
    form.validateFields().then(values => {

      const params = _handleFormValue(values);

      onSearch?.(params);
    }).catch(errorInfo => {
      console.log('表单验证失败:', errorInfo);
    });
  };
  const handleExport = () => {
    form.validateFields().then(values => {
      const params = _handleFormValue(values);

      onExport?.(params);
    })
  };
  // const handleViewExportList = () => {
  //   setIsModalVisible(true);
  // };
  // const handleCancel = () => {
  //   setIsModalVisible(false);
  // };

  return (
    <>
      <Form form={form} layout="inline" className={styles['filter-form']}>
        <Row gutter={24} className={styles['filter-form__item-row']}>
          <Col flex="1">
            <Form.Item
              label="报告周期"
              name="reportPeriod"
              className={styles['filter-form__item']}
              rules={[
                { required: true, message: '请选择报告周期' }
              ]}
            >
              <RangePicker
                className={styles['filter-form__control']}
                placeholder={["请选择开始时间", "请选择结束时间"]}
                onChange={handleDateRangeChange}
              />
            </Form.Item>
          </Col>

          <Col flex="1">
            <Form.Item
              label="车队选择"
              name="fleet_names"
              className={styles['filter-form__item']}
              rules={[
                { required: true, message: '请选择车队' }
              ]}
            >
              <Select
                mode="multiple"
                placeholder="请选择车队（必填）"
                options={allCompanyList.map(item => ({ label: item.name, value: item.name }))}
                className={styles['filter-form__control']}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                }
                maxTagCount="responsive"
                onChange={handleFleetChange}
              >
              </Select>
            </Form.Item>
          </Col>

          <Col flex="1">
            <Form.Item label="服务商" name="insurance_company" className={styles['filter-form__item']}>
              <Select
                placeholder="请选择服务商"
                allowClear
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                }
                options={insurerOptions}
                className={styles['filter-form__control']}
                onChange={handleInsurerChange}
              />
            </Form.Item>
          </Col>

          <Col flex="1">
            <Form.Item label="车辆选择" name="plate_numbers" className={styles['filter-form__item']}>
              <Select
                placeholder="请选择"
                allowClear
                mode='multiple'
                className={styles['filter-form__control']}
                options={carOptions}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                }
                maxTagCount="responsive"
              >
              </Select>
            </Form.Item>
          </Col>

          <Col flex="1">
            <Form.Item label="报告显示名称" name="report_name" rules={getReportNameRules()} className={styles['filter-form__item']}>
              <Input placeholder="请输入报告显示名称" allowClear className={styles['filter-form__control']} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24} className={styles['filter-form__item-row']}>
          <Col span={24} className={styles['filter-form__actions']}>
            {isSearching && <span className={styles['filter-form__status-text']}>正在查询，请稍候...</span>}
            {isExporting && <span className={styles['filter-form__status-text']}>正在导出，请稍候...</span>}
            <Button type="primary" onClick={handleSearch} className={styles['filter-form__btn']} loading={loading || isSearching} disabled={loading || isSearching}>
              搜索
            </Button>
            <Button onClick={handleExport} className={styles['filter-form__btn']} loading={isExporting} disabled={isExporting}>
              导出
            </Button>
            {/* <Button type="primary" ghost onClick={handleViewExportList}>
              查看导出列表
            </Button> */}
          </Col>
        </Row>
      </Form>
      {/* <Modal title="导出列表" open={isModalVisible} onCancel={handleCancel} footer={null} width={1000}>
        <ExportListModalContent />
      </Modal> */}
    </>
  );
}
